import { FunctionalComponent } from 'preact'
import { Activity, Globe, Loader2, Lock, Plus, X } from 'lucide-preact'
import { TextBox } from '@/components'
import { APPROVED_DOMAINS_MAX_COUNT } from '@/utils/approvedDomain'
import type {
  CompanyTrackingViewDto,
  DerivedApprovedDomain,
  DerivedApprovedDomainSource,
} from '@/types/tracking'
import type { ApiResponse } from '@/api/client'
import { useApprovedDomainsEditor, type ApprovedDomainsEditor } from './useApprovedDomainsEditor'

interface ApprovedDomainsPanelProps {
  tracking: CompanyTrackingViewDto
  onSave: (domains: string[]) => Promise<ApiResponse<CompanyTrackingViewDto> | null>
}

// Ring-style source badges, matching the DomainSuppressionList convention.
const DERIVED_BADGE: Record<DerivedApprovedDomainSource, { label: string; className: string }> = {
  company_profile: {
    label: 'Company profile',
    className: 'text-sky-700 dark:text-sky-300 bg-sky-50 dark:bg-sky-950/40 ring-sky-600/20',
  },
  custom_landing_page: {
    label: 'Custom landing page',
    className: 'text-violet-700 dark:text-violet-300 bg-violet-50 dark:bg-violet-950/40 ring-violet-600/20',
  },
}

const CUSTOM_BADGE = 'text-slate-600 dark:text-slate-300 bg-slate-50 dark:bg-slate-800/60 ring-slate-300/60 dark:ring-slate-600/60'

function SourceBadge({ label, className }: { label: string; className: string }) {
  return (
    <span className={`inline-flex rounded-full px-2 py-0.5 text-xs font-medium ring-1 ring-inset ${className}`}>
      {label}
    </span>
  )
}

function LastEventIndicator({ lastEvent }: { lastEvent: string | null }) {
  return (
    <div
      className={`inline-flex items-center gap-1.5 text-xs ${
        lastEvent ? 'text-primary-600 dark:text-primary-400' : 'text-slate-400 dark:text-slate-500'
      }`}
      title={lastEvent ? new Date(lastEvent).toLocaleString() : undefined}
    >
      <Activity className="w-3.5 h-3.5 shrink-0" />
      {lastEvent
        ? `Last event received ${new Date(lastEvent).toLocaleString()}`
        : 'No tracking events received yet'}
    </div>
  )
}

function DerivedRow({ domain }: { domain: DerivedApprovedDomain }) {
  const badge = DERIVED_BADGE[domain.Source]
  return (
    <li className="flex items-center gap-3 px-4 py-3">
      <Globe className="w-4 h-4 shrink-0 text-slate-400" />
      <span className="flex-1 min-w-0 truncate font-medium text-slate-900 dark:text-white" title={domain.Host}>
        {domain.Host}
      </span>
      {badge && <SourceBadge label={badge.label} className={badge.className} />}
      <Lock className="w-3.5 h-3.5 shrink-0 text-slate-400" aria-label="Read-only" />
    </li>
  )
}

function CustomRow({
  domain,
  isRemoving,
  disabled,
  onRemove,
}: {
  domain: string
  isRemoving: boolean
  disabled: boolean
  onRemove: () => void
}) {
  return (
    <li className="flex items-center gap-3 px-4 py-3">
      <Globe className="w-4 h-4 shrink-0 text-slate-400" />
      <span className="flex-1 min-w-0 truncate font-medium text-slate-900 dark:text-white" title={domain}>
        {domain}
      </span>
      <SourceBadge label="Added manually" className={CUSTOM_BADGE} />
      <button
        type="button"
        onClick={onRemove}
        disabled={disabled}
        aria-label={`Remove ${domain}`}
        className="inline-flex items-center justify-center w-7 h-7 rounded-lg text-slate-400 hover:text-red-600 dark:hover:text-red-400 hover:bg-red-50 dark:hover:bg-red-900/20 transition-colors disabled:opacity-50"
      >
        {isRemoving ? <Loader2 className="w-4 h-4 animate-spin" /> : <X className="w-4 h-4" />}
      </button>
    </li>
  )
}

function AddDomainForm({ editor }: { editor: ApprovedDomainsEditor }) {
  const { newDomain, error, saving, setNewDomain, clearError, handleAdd, closeAddForm } = editor
  return (
    <div className="flex flex-col sm:flex-row sm:items-start gap-2">
      <div className="flex-1 min-w-0">
        <TextBox
          name="new-approved-domain"
          value={newDomain}
          onChange={(next) => { setNewDomain(next); clearError() }}
          onKeyDown={(e) => {
            if (e.key === 'Enter' && !saving) void handleAdd()
            if (e.key === 'Escape') closeAddForm()
          }}
          error={error ?? undefined}
          placeholder="example.com"
          density="compact"
          disabled={saving}
          autoFocus
        />
      </div>
      <div className="flex items-center gap-2 shrink-0">
        <button
          type="button"
          onClick={() => { void handleAdd() }}
          disabled={saving || !newDomain.trim()}
          className="inline-flex items-center gap-1.5 px-3 py-2 text-sm font-medium rounded-lg bg-primary-600 text-white hover:bg-primary-700 transition-colors disabled:opacity-50"
        >
          {saving && <Loader2 className="w-4 h-4 animate-spin" />} Add
        </button>
        <button
          type="button"
          onClick={closeAddForm}
          className="px-3 py-2 text-sm rounded-lg text-slate-600 dark:text-slate-300 hover:bg-slate-100 dark:hover:bg-slate-700 transition-colors"
        >
          Done
        </button>
      </div>
    </div>
  )
}

/**
 * Approved-domains allowlist on the visitor-tracking panel (ORB-458). Shows read-only, system-derived
 * rows (company profile + External-mode landing pages) with source badges and a lock, and the editable
 * custom list with add/remove — each add/remove persists via PATCH. The always-on platform host is not
 * listed; it is covered by the static footer note. State, validation, and save orchestration live in
 * useApprovedDomainsEditor; this component is presentation only.
 */
export const ApprovedDomainsPanel: FunctionalComponent<ApprovedDomainsPanelProps> = ({
  tracking,
  onSave,
}) => {
  const editor = useApprovedDomainsEditor(tracking, onSave)
  const { derived, custom, adding, saving, removing, atLimit, openAddForm } = editor

  return (
    <div className="space-y-4">
      <div className="flex items-start justify-between flex-wrap gap-3">
        <div>
          <h3 className="text-lg font-semibold text-slate-900 dark:text-white">Approved Domains</h3>
          <p className="text-sm text-slate-600 dark:text-slate-300 mt-1 max-w-prose">
            Origins allowed to send tracking events for this company. Derived domains are managed for you;
            add any additional install hosts below.
          </p>
        </div>
        <LastEventIndicator lastEvent={tracking.LastEventReceived ?? null} />
      </div>

      <ul className="divide-y divide-slate-100 dark:divide-slate-800 rounded-xl ring-1 ring-slate-200 dark:ring-slate-800 bg-white dark:bg-slate-900">
        {derived.map((domain) => (
          <DerivedRow key={`derived:${domain.Host}`} domain={domain} />
        ))}

        {custom.map((domain) => (
          <CustomRow
            key={`custom:${domain}`}
            domain={domain}
            isRemoving={removing === domain}
            disabled={removing === domain || saving}
            onRemove={() => { void editor.handleRemove(domain) }}
          />
        ))}

        {derived.length === 0 && custom.length === 0 && (
          <li className="px-4 py-3 text-sm text-slate-500 dark:text-slate-400">
            No approved domains yet.
          </li>
        )}
      </ul>

      {adding ? (
        <AddDomainForm editor={editor} />
      ) : (
        <button
          type="button"
          onClick={openAddForm}
          disabled={atLimit}
          className="inline-flex items-center gap-2 px-3 py-2 text-sm font-medium rounded-lg border border-slate-300 dark:border-slate-700 text-slate-700 dark:text-slate-200 hover:bg-slate-50 dark:hover:bg-slate-800 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
          title={atLimit ? `Limit of ${APPROVED_DOMAINS_MAX_COUNT} approved domains reached` : undefined}
        >
          <Plus className="w-4 h-4" /> Add domain
        </button>
      )}

      <p className="text-xs text-slate-400 dark:text-slate-500 max-w-prose leading-relaxed">
        Platform landing pages are always tracked while tracking is on. To stop tracking one, disable it
        from that landing page&apos;s settings.
      </p>
    </div>
  )
}
